import type { KeyboardLayout } from './keyboard.types';

/**
 * Complete keyboard layouts for all African languages
 * Includes all characters, diacritics, digraphs, and special symbols
 */

/**
 * Build comprehensive African language layout
 * Covers: Ewe, Mina, Kabiyè, Kotokoli, Loso, Tem, Bassar, Nawdem, Moba, Fon
 */
function buildAfricanLayout(): KeyboardLayout {
  return {
    // Default lowercase - African base characters
    default: [
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      '{space}',
    ],

    // Uppercase
    uppercase: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      '{space}',
    ],

    // African special characters - comprehensive set
    african: [
      'ɛ ɔ ʋ ŋ ɣ ɖ ƒ ɩ ə ɵ',
      'ɓ ɗ ɠ ʊ ɲ ʃ ǝ ɑ ʉ ƴ',
      'ẹ ọ ṣ gb kp ny hw xw',
      'á à ǎ â ã ā é è ě ê',
      'ẽ ē í ì ǐ î ĩ ī ó ò',
      'ǒ ô õ ō ú ù ǔ û ũ ū',
      'ɛ́ ɛ̀ ɛ̌ ɛ̂ ɛ̃ ɛ̄ ɔ́ ɔ̀ ɔ̌ ɔ̂',
      'ɔ̃ ɔ̄ ʋ́ ʋ̀ ʋ̌ ʋ̂ ʋ̃ ʋ̄',
      'ẹ́ ẹ̀ ẹ̄ ọ́ ọ̀ ọ̄ ṣ́ ṣ̀ ṣ̄',
      'ɩ́ ɩ̀ ɩ̄ ʊ́ ʊ̀ ʊ̄ ə́ ə̀ ə̄',
      '{shift} {space} {bksp}',
    ],

    // African uppercase
    africanUpper: [
      'Ɛ Ɔ Ʋ Ŋ Ɣ Ɖ Ƒ Ɩ Ə Ɵ',
      'Ɓ Ɗ Ɠ Ʊ Ɲ Ʃ Ǝ Ɑ Ʉ Ƴ',
      'Ẹ Ọ Ṣ Gb Kp Ny Hw Xw',
      'Á À Ǎ Â Ã Ā É È Ě Ê',
      'Ẽ Ē Í Ì Ǐ Î Ĩ Ī Ó Ò',
      'Ǒ Ô Õ Ō Ú Ù Ǔ Û Ũ Ū',
      'Ɛ́ Ɛ̀ Ɛ̌ Ɛ̂ Ɛ̃ Ɛ̄ Ɔ́ Ɔ̀ Ɔ̌ Ɔ̂',
      'Ɔ̃ Ɔ̄ Ʋ́ Ʋ̀ Ʋ̌ Ʋ̂ Ʋ̃ Ʋ̄',
      'Ẹ́ Ẹ̀ Ẹ̄ Ọ́ Ọ̀ Ọ̄ Ṣ́ Ṣ̀ Ṣ̄',
      'Ɩ́ Ɩ̀ Ɩ̄ Ʊ́ Ʊ̀ Ʊ̄ Ə́ Ə̀ Ə̄',
      '{shift} {space} {bksp}',
    ],

    // Punctuation & numbers
    punctuation: [
      '1 2 3 4 5 6 7 8 9 0',
      '! @ # $ % ^ & * ( )',
      `. , ; : ? " ' - + =`,
      '/ \\ | [ ] { } < >',
      `« » " " ' ' … — – _`,
      '{space} {bksp}',
    ],

    // Diacritics layer - quick access to all tonal marks
    diacritics: [
      'á é í ó ú ɛ́ ɔ́ ʋ́ ɩ́ ʊ́',
      'à è ì ò ù ɛ̀ ɔ̀ ʋ̀ ɩ̀ ʊ̀',
      'ǎ ě ǐ ǒ ǔ ɛ̌ ɔ̌ ʋ̌ ɩ̌ ʊ̌',
      'â ê î ô û ɛ̂ ɔ̂ ʋ̂ ɩ̂ ʊ̂',
      'ã ẽ ĩ õ ũ ɛ̃ ɔ̃ ʋ̃ ɩ̃ ʊ̃',
      'ā ē ī ō ū ɛ̄ ɔ̄ ʋ̄ ɩ̄ ʊ̄',
      '{space} {bksp}',
    ],
  };
}

/**
 * French keyboard layout (AZERTY)
 * Includes all French diacritics and special characters
 */
const FRENCH_LAYOUT: KeyboardLayout = {
  default: [
    'a z e r t y u i o p',
    'q s d f g h j k l m',
    '{shift} w x c v b n ç {bksp}',
    '{space}',
  ],

  uppercase: [
    'A Z E R T Y U I O P',
    'Q S D F G H J K L M',
    '{shift} W X C V B N Ç {bksp}',
    '{space}',
  ],

  // French with accents
  african: [
    'à â é è ê ë î ï ô ù',
    'û ü ÿ æ œ ç',
    'á í ó ú ñ',
    '{shift} {space} {bksp}',
  ],

  africanUpper: [
    'À Â É È Ê Ë Î Ï Ô Ù',
    'Û Ü Ÿ Æ Œ Ç',
    'Á Í Ó Ú Ñ',
    '{shift} {space} {bksp}',
  ],

  punctuation: [
    '1 2 3 4 5 6 7 8 9 0',
    '! @ # € % ^ & * ( )',
    `. , ; : ? " ' - + =`,
    '/ \\ | [ ] { } < >',
    `« » " " ' ' … — – _`,
    '{space} {bksp}',
  ],

  diacritics: [
    'á é í ó ú à è ì ò ù',
    'â ê î ô û ä ë ï ö ü',
    'ã õ ñ ç æ œ ÿ',
    '{space} {bksp}',
  ],
};

/**
 * Yoruba-specific layout
 * Focus on underdotted characters (ẹ, ọ, ṣ) + tonal marks
 */
const YORUBA_LAYOUT: KeyboardLayout = {
  default: [
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m {bksp}',
    '{space}',
  ],

  uppercase: [
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M {bksp}',
    '{space}',
  ],

  african: [
    'ẹ ọ ṣ ɛ ɔ ŋ gb kp',
    'ẹ́ ẹ̀ ẹ̄ ọ́ ọ̀ ọ̄ ṣ́ ṣ̀ ṣ̄',
    'á à ā é è ē í ì ī',
    'ó ò ō ú ù ū',
    'ɛ́ ɛ̀ ɛ̄ ɔ́ ɔ̀ ɔ̄',
    '{shift} {space} {bksp}',
  ],

  africanUpper: [
    'Ẹ Ọ Ṣ Ɛ Ɔ Ŋ Gb Kp GB KP',
    'Ẹ́ Ẹ̀ Ẹ̄ Ọ́ Ọ̀ Ọ̄ Ṣ́ Ṣ̀ Ṣ̄',
    'Á À Ā É È Ē Í Ì Ī',
    'Ó Ò Ō Ú Ù Ū',
    'Ɛ́ Ɛ̀ Ɛ̄ Ɔ́ Ɔ̀ Ɔ̄',
    '{shift} {space} {bksp}',
  ],

  punctuation: [
    '1 2 3 4 5 6 7 8 9 0',
    '! @ # $ % ^ & * ( )',
    `. , ; : ? " ' - + =`,
    '/ \\ | [ ] { } < >',
    `« » " " ' ' … — – _`,
    '{space} {bksp}',
  ],

  diacritics: [
    'á à ā é è ē í ì ī ó ò ō ú ù ū',
    'ẹ́ ẹ̀ ẹ̄ ọ́ ọ̀ ọ̄ ṣ́ ṣ̀ ṣ̄',
    'ɛ́ ɛ̀ ɛ̄ ɔ́ ɔ̀ ɔ̄',
    '{space} {bksp}',
  ],
};

/**
 * Fulfulde/Peul layout
 * Focus on implosives (ɓ, ɗ) and hooked characters (ƴ)
 */
const FULFULDE_LAYOUT: KeyboardLayout = {
  default: [
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m {bksp}',
    '{space}',
  ],

  uppercase: [
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M {bksp}',
    '{space}',
  ],

  african: [
    'ɓ ɗ ŋ ɲ ƴ ʼ',
    'nb nd nj ng ny',
    'á à â ã é è ê ẽ í ì î ĩ',
    'ó ò ô õ ú ù û ũ',
    'aa ee ii oo uu',
    '{shift} {space} {bksp}',
  ],

  africanUpper: [
    'Ɓ Ɗ Ŋ Ɲ Ƴ ʼ',
    'Nb Nd Nj Ng Ny',
    'Á À Â Ã É È Ê Ẽ Í Ì Î Ĩ',
    'Ó Ò Ô Õ Ú Ù Û Ũ',
    'AA EE II OO UU',
    '{shift} {space} {bksp}',
  ],

  punctuation: [
    '1 2 3 4 5 6 7 8 9 0',
    '! @ # $ % ^ & * ( )',
    `. , ; : ? " ' - + =`,
    '/ \\ | [ ] { } < >',
    `« » " " ' ' … — – _`,
    '{space} {bksp}',
  ],

  diacritics: [
    'á à â ã é è ê ẽ í ì î ĩ',
    'ó ò ô õ ú ù û ũ',
    '{space} {bksp}',
  ],
};

/**
 * Bambara layout
 * Focus on nasalization and tone marking
 */
const BAMBARA_LAYOUT: KeyboardLayout = {
  default: [
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m {bksp}',
    '{space}',
  ],

  uppercase: [
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M {bksp}',
    '{space}',
  ],

  african: [
    'ɛ ɔ ɲ ŋ',
    'á à ã é è ẽ ɛ́ ɛ̀ ɛ̃',
    'í ì ĩ ó ò õ ɔ́ ɔ̀ ɔ̃',
    'ú ù ũ',
    '{shift} {space} {bksp}',
  ],

  africanUpper: [
    'Ɛ Ɔ Ɲ Ŋ',
    'Á À Ã É È Ẽ Ɛ́ Ɛ̀ Ɛ̃',
    'Í Ì Ĩ Ó Ò Õ Ɔ́ Ɔ̀ Ɔ̃',
    'Ú Ù Ũ',
    '{shift} {space} {bksp}',
  ],

  punctuation: [
    '1 2 3 4 5 6 7 8 9 0',
    '! @ # $ % ^ & * ( )',
    `. , ; : ? " ' - + =`,
    '/ \\ | [ ] { } < >',
    `« » " " ' ' … — – _`,
    '{space} {bksp}',
  ],

  diacritics: [
    'á à ã é è ẽ í ì ĩ',
    'ó ò õ ú ù ũ',
    'ɛ́ ɛ̀ ɛ̃ ɔ́ ɔ̀ ɔ̃',
    '{space} {bksp}',
  ],
};

/**
 * Master layout registry
 * Maps language names to their keyboard layouts
 */
export const KEYBOARD_LAYOUTS: Record<string, KeyboardLayout> = {
  // French
  français: FRENCH_LAYOUT,
  french: FRENCH_LAYOUT,

  // Yoruba
  yoruba: YORUBA_LAYOUT,

  // Fulfulde
  fulfulde: FULFULDE_LAYOUT,
  peul: FULFULDE_LAYOUT,
  fula: FULFULDE_LAYOUT,

  // Bambara
  bambara: BAMBARA_LAYOUT,

  // All other African languages use comprehensive African layout
  ewe: buildAfricanLayout(),
  mina: buildAfricanLayout(),
  kabye: buildAfricanLayout(),
  kabiyè: buildAfricanLayout(),
  kotokoli: buildAfricanLayout(),
  loso: buildAfricanLayout(),
  tem: buildAfricanLayout(),
  bassar: buildAfricanLayout(),
  nawdem: buildAfricanLayout(),
  moba: buildAfricanLayout(),
  fon: buildAfricanLayout(),
};

/**
 * Resolve layout for a given language
 * @param language - Language name (case-insensitive)
 * @returns Keyboard layout for the language, defaults to African layout
 */
export function resolveLayout(language: string): KeyboardLayout {
  const key = language.toLowerCase().trim();
  return KEYBOARD_LAYOUTS[key] ?? buildAfricanLayout();
}

/**
 * Check if a language should use French as default
 */
export function isFrenchLanguage(language: string): boolean {
  const key = language.toLowerCase().trim();
  return key === 'français' || key === 'french';
}
